import type { Identifier, XYCoord } from 'dnd-core';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';

import { ItemTypeOne } from './ItemTypes'
import { Box, Stack, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

const style = {
    border: '1px dashed gray',
    cursor: 'move',
    margin: 8,
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    borderRadius: 0,
}));

const handleStyle: CSSProperties = {
    backgroundColor: 'green',
    width: '1rem',
    height: '1rem',
    display: 'inline-block',
    marginRight: '0.75rem',
    cursor: 'move',
}

export interface CardProps {
    id: any
    text: string
    index: number
    moveCard: (dragIndex: number, hoverIndex: number) => void
}

interface DragItem {
    index: number
    id: string
    type: string
}

export const Card = (props: any) => {
    const ref = useRef<HTMLDivElement>(null)
    const [{ handlerId }, drop] = useDrop<
        DragItem,
        void,
        { handlerId: Identifier | null }
    >({
        accept: ItemTypeOne.CARD,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            }
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return
            }
            const dragIndex = item.index
            const hoverIndex = props?.index

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect()

            // Get vertical middle
            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

            // Determine mouse position
            const clientOffset = monitor.getClientOffset()

            // Get pixels to the top
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return
            }

            // Time to actually perform the action
            props?.moveCard(dragIndex, hoverIndex)

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex
        },
    })

    const [{ isDragging }, drag] = useDrag({
        type: ItemTypeOne.CARD,
        item: () => {
            return { id: props?.id, index: props?.index }
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    })

    const opacity = isDragging ? 0 : 1
    drag(drop(ref))
    return (
        <Stack direction="row" spacing={0} ref={ref} data-handler-id={handlerId} style={{ ...style, opacity }}>
            <Item sx={{ flexGrow: 1, textTransform: 'capitalize' }}> <Typography variant='h5' gutterBottom>{props?.text}</Typography> </Item>
            <Item sx={{ textTransform: 'capitalize' }}><Button variant="outlined" startIcon={<DeleteIcon />} onClick={() => { props?.setInputId(props?.id) }}>Edit</Button></Item>
            <Item sx={{ textTransform: 'capitalize' }}><Button variant="outlined" startIcon={<DeleteIcon />} onClick={() => {
                props?.setItems((prevItems: any) => [...prevItems].filter(e => e.id != props.id))
            }}>Delete</Button></Item>
        </Stack>
    )
}
